import React, { FunctionComponent, useState } from 'react';
import {
  Autocomplete,
  Button,
  Grid,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { ILessonStudentFeedback } from '../../../store/V2/LessonStudentFeedbacksSlice';
import { CustomCardContent, IFeedbackStudent, IFeedbackTutor } from './helper';
import { TPAutoCompleteArrowIconComponent, TPTextField } from '../../Inputs';
import { getShortName } from '../../../helper';
import { IUser } from '../../../store/UserSlice';
import { ILessonTutorFeedback } from '../../../store/V2/LessonTutorFeedbacksSlice';
import AddTutorFeedbackModal from './AddTutorFeedbackModal';

interface GroupClassTutorFeedbackContentsForTutorProps {
  feedbacks: ILessonTutorFeedback[];
  students: IFeedbackStudent[];
  sectionId: string;
  onFeedbackAdded: (feedbackData: ILessonTutorFeedback[]) => void;
  onFeedbackUpdated: (id: string, feedbackData: ILessonTutorFeedback) => void;
}

const actionButtonStyles: SxProps = {
  padding: { xs: '6px 12px', sm: '12px 24px' },
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
};

const allUserOption: IFeedbackStudent = {
  _id: 'All',
  firstname: 'All',
};

const GroupClassTutorFeedbackContentsForTutor: FunctionComponent<GroupClassTutorFeedbackContentsForTutorProps> = ({
  feedbacks,
  students,
  sectionId,
  onFeedbackAdded,
  onFeedbackUpdated,
}) => {
  // State
  const [selectedStudents, setSelectedStudents] = useState<IFeedbackStudent[]>(
    []
  );
  const [addModalData, setAddModalData] = useState<{
    open: boolean;
    users?: IFeedbackStudent[];
  }>({
    open: false,
  });

  const [editModalData, setEditModalData] = useState<{
    open: boolean;
    feedback?: ILessonTutorFeedback;
  }>({
    open: false,
  });

  const studentOptions = [...students].filter((studentItem) => {
    return (
      feedbacks.some((feedbackItem) => {
        const _feedbackStudent = feedbackItem.student as IUser;

        return _feedbackStudent._id === studentItem._id;
      }) === false
    );
  });

  if (studentOptions.length > 1) {
    studentOptions.unshift({ ...allUserOption });
  }

  const openAddFeedbackModal = () => {
    setAddModalData({
      open: true,
      users: selectedStudents.some((item) => item._id === allUserOption._id)
        ? [...studentOptions].filter((item) => item._id !== allUserOption._id)
        : [...selectedStudents],
    });
  };

  return (
    <div>
      <Grid container rowSpacing="20px">
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" columnGap="10px">
            <Autocomplete
              multiple
              fullWidth
              disableClearable
              filterSelectedOptions
              value={selectedStudents}
              options={[...studentOptions]}
              popupIcon={TPAutoCompleteArrowIconComponent}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(e, value) => {
                if (value.some((item) => item._id === allUserOption._id)) {
                  setSelectedStudents([allUserOption]);
                } else {
                  setSelectedStudents(value);
                }
              }}
              getOptionLabel={(option) =>
                getShortName(option.firstname, option.lastname)
              }
              getOptionDisabled={() => {
                if (
                  selectedStudents.some(
                    (item) => item._id === allUserOption._id
                  )
                ) {
                  return true;
                }

                return false;
              }}
              renderInput={(params) => (
                <TPTextField {...params} placeholder="Students" />
              )}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ ...actionButtonStyles }}
              disabled={!selectedStudents.length}
              onClick={openAddFeedbackModal}
            >
              Add
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <div>
            <Typography variant="h6">Feedbacks</Typography>
          </div>
        </Grid>
        {feedbacks.map((item) => {
          const feedbackStudent = item.student as IUser;
          const studentName = getShortName(
            feedbackStudent.firstname,
            feedbackStudent.lastname
          );

          return (
            <Grid item xs={12} key={item._id}>
              <CustomCardContent>
                <Stack direction="row" alignItems="center">
                  <Typography flexGrow={1} className="ellipsis-text one-line">
                    Feedback - {studentName}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    color="inherit"
                    sx={{ ...actionButtonStyles, padding: undefined }}
                    onClick={() =>
                      setEditModalData({
                        open: true,
                        feedback: item,
                      })
                    }
                  >
                    Edit
                  </Button>
                </Stack>
              </CustomCardContent>
            </Grid>
          );
        })}
        {!feedbacks.length ? (
          <div>
            <Typography>No Feedbacks are available</Typography>
          </div>
        ) : null}
      </Grid>
      {addModalData.open && addModalData.users && addModalData.users.length ? (
        <AddTutorFeedbackModal
          open={addModalData.open}
          sectionId={sectionId}
          students={addModalData.users}
          onFeedbackAdded={(feedbackData) => {
            onFeedbackAdded(feedbackData);
            setSelectedStudents([]);
          }}
          onClose={() => setAddModalData({ open: false })}
        />
      ) : null}
      {editModalData.open && editModalData.feedback ? (
        <AddTutorFeedbackModal
          open={editModalData.open}
          sectionId={sectionId}
          feedback={editModalData.feedback}
          onFeedbackUpdated={onFeedbackUpdated}
          onClose={() => setEditModalData({ open: false })}
        />
      ) : null}
    </div>
  );
};

export default GroupClassTutorFeedbackContentsForTutor;
